/**
 * 1.6.7 (D-1.6.7-1) — the Before photos on the Finish sheet: the photos the Arrived step took, shown above the
 * After photos so the crew can match the angle. Read-only; a press shows one larger. Nothing here leaves the
 * visit's own Before/ folder (GET /api/finish/photo serves only that).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

export const beforePhotoUrl = (appointmentId: string, path: string) => `/api/finish/photo?appointmentId=${encodeURIComponent(appointmentId)}&path=${encodeURIComponent(path)}`;

export function BeforePhotos({ appointmentId, photos }: { appointmentId: string; photos: readonly { name: string; path: string }[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  if (!photos.length) return null;
  const count = photos.length;
  const shown = open === null ? null : photos[open];
  return (
    <section className="field ba-before" aria-labelledby="ba-before-h" data-testid="before-taken">
      <h3 className="eyebrow" id="ba-before-h">
        {t('ba167.beforeTitle')}
      </h3>
      <ul className="ba-before-list">
        {photos.map((p, i) => (
          <li key={p.path}>
            <button
              type="button"
              className="ba-before-btn"
              aria-label={t('ba167.beforeOpen', { n: i + 1, count })}
              onClick={(e) => {
                opener.current = e.currentTarget;
                setOpen(i);
              }}
              data-testid={`before-taken-${i + 1}`}
            >
              <img src={beforePhotoUrl(appointmentId, p.path)} alt={t('ba167.beforeN', { n: i + 1, count })} loading="lazy" />
            </button>
          </li>
        ))}
      </ul>
      <p className="help">{t('ba167.beforeHelp')}</p>
      <Dialog open={shown !== null && shown !== undefined} onOpenChange={(o) => (!o ? setOpen(null) : undefined)}>
        {shown ? (
          <DialogContent
            className="ba-before-dialog"
            data-testid="before-taken-large"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              opener.current?.focus();
            }}
          >
            <DialogTitle>{t('ba167.beforeLarge', { n: (open ?? 0) + 1, count })}</DialogTitle>
            <DialogDescription>{t('ba167.beforeLargeHelp')}</DialogDescription>
            <img src={beforePhotoUrl(appointmentId, shown.path)} alt={t('ba167.beforeN', { n: (open ?? 0) + 1, count })} />
            <Button variant="secondary" onClick={() => setOpen(null)} data-testid="before-taken-close">
              {t('ba167.close')}
            </Button>
          </DialogContent>
        ) : null}
      </Dialog>
    </section>
  );
}
